import { cur } from '@/lib/currency';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MenuListRow } from '@/components/menu/MenuListRow';
import { MenuGridCard } from '@/components/menu/MenuGridCard';
import { BulkEditToolbar } from '@/components/menu/BulkEditToolbar';
import { DuplicateMenuDialog } from '@/components/menu/DuplicateMenuDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, X, UtensilsCrossed,
  Loader2, Star, Upload, Building2, Globe, Check, Download, Tag, Package, ListChecks, Clock,
  LayoutGrid, List as ListIcon, Copy, Sparkles, FolderInput
} from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBranchStore } from '@/stores';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import MenuItemEditSheet from '@/components/menu/MenuItemEditSheet';

const DEFAULT_CATEGORIES = [
  { id: 'main', name: 'Main Dishes' },
  { id: 'appetizer', name: 'Appetizers' },
  { id: 'beverage', name: 'Beverages' },
  { id: 'dessert', name: 'Desserts' },
  { id: 'sides', name: 'Sides' },
];

// ---- Small sub-components for the editor tabs ----

function SizesEditor({ sizes, onChange, branches, branchAvailability, onAvailChange, branchPrices, onBranchPricesChange, recipeLinks = [], recipeById = {} }) {
  const addSize = () => onChange([...sizes, { name: '', price: 0 }]);
  const remove = (i) => onChange(sizes.filter((_, j) => j !== i));
  const update = (i, field, val) => {
    const next = [...sizes];
    next[i] = { ...next[i], [field]: field === 'price' ? (parseFloat(val) || 0) : val };
    onChange(next);
  };

  // Per-Size Cost — auto-cost for a size = its own recipe link (else the
  // default link) recipe cost_per_unit × scale. Read-only insight.
  const sizeCost = (sizeName) => {
    const link = recipeLinks.find(l => (l.size_name || '') === sizeName && l.recipe_id)
      || recipeLinks.find(l => !(l.size_name || '') && l.recipe_id);
    if (!link) return null;
    const r = recipeById[link.recipe_id];
    if (!r || r.cost_per_unit == null) return null;
    return Number(r.cost_per_unit) * Number(link.scale || 1);
  };

  const setBranchPrice = (sizeName, branchId, price) => {
    const next = { ...(branchPrices || {}) };
    const key = `${sizeName}__${branchId}`;
    next[key] = parseFloat(price) || 0;
    onBranchPricesChange(next);
  };

  const getBranchPrice = (sizeName, branchId) => {
    const key = `${sizeName}__${branchId}`;
    return (branchPrices || {})[key];
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-info/10 dark:bg-blue-900/10 rounded-lg">
        <p className="text-xs text-info dark:text-blue-400">Add size variants with branch-specific pricing. Toggle which branches offer each size and set custom prices per branch.</p>
      </div>
      {sizes.map((size, i) => (
        <div key={i} className="border rounded-xl p-4 space-y-3 bg-white dark:bg-muted">
          <div className="flex items-center gap-3">
            <Input placeholder="Size name (e.g., Small)" value={size.name} onChange={e => update(i, 'name', e.target.value)} className="flex-1 font-semibold rounded-lg" data-testid={`size-name-${i}`} />
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Base:</span>
              <Input type="number" step="0.01" placeholder="Price" value={size.price} onChange={e => update(i, 'price', e.target.value)} className="w-24 rounded-lg" data-testid={`size-price-${i}`} />
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-error rounded-lg" onClick={() => remove(i)}><Trash2 size={14} /></Button>
          </div>
          {(() => {
            const c = sizeCost(size.name);
            if (c == null) return null;
            const p = Number(size.price) || 0;
            const m = p > 0 ? ((p - c) / p) * 100 : null;
            return (
              <div className="text-[11px] flex items-center gap-2" data-testid={`size-cost-${i}`}>
                <span className="text-emerald-700 dark:text-emerald-400 font-medium">Auto-cost: SR {c.toFixed(2)}</span>
                {m != null && <span className={m < 30 ? 'text-rose-600' : 'text-muted-foreground'}>· margin {m.toFixed(0)}%</span>}
                <span className="text-muted-foreground">(from linked recipe)</span>
              </div>
            );
          })()}
          {size.name && branches.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-[11px] font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Branch Availability & Pricing</p>
              <div className="space-y-1.5">
                {branches.map(b => {
                  const avail = branchAvailability[b.id] || [];
                  const isOn = avail.includes(size.name);
                  const customPrice = getBranchPrice(size.name, b.id);
                  return (
                    <div key={b.id} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isOn ? 'bg-primary/5' : 'bg-muted/50 dark:bg-stone-700/50'}`}>
                      <Checkbox
                        checked={isOn}
                        onCheckedChange={() => {
                          const next = { ...branchAvailability };
                          const curr = next[b.id] || [];
                          next[b.id] = isOn ? curr.filter(s => s !== size.name) : [...curr, size.name];
                          onAvailChange(next);
                        }}
                        data-testid={`size-branch-${b.id}-${i}`}
                      />
                      <span className={`text-xs font-medium flex-1 ${isOn ? 'text-foreground dark:text-stone-200' : 'text-muted-foreground'}`}>{b.name}</span>
                      {isOn && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground">SR</span>
                          <Input
                            type="number" step="0.01"
                            placeholder={String(size.price)}
                            value={customPrice !== undefined ? customPrice : ''}
                            onChange={e => setBranchPrice(size.name, b.id, e.target.value)}
                            className="w-20 h-7 text-xs rounded-md"
                            data-testid={`size-branch-price-${b.id}-${i}`}
                          />
                          {customPrice !== undefined && customPrice !== size.price && (
                            <span className="text-[9px] text-primary font-medium">custom</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" className="w-full rounded-xl" onClick={addSize} data-testid="add-size-btn"><Plus size={16} className="mr-1" /> Add Size</Button>
    </div>
  );
}

function AddonsLinker({ linkedAddons, onChange, centralAddons, branches, branchAvailability, onAvailChange }) {
  const available = centralAddons.filter(a => a.is_active !== false);
  const toggle = (addonId) => {
    onChange(linkedAddons.includes(addonId) ? linkedAddons.filter(id => id !== addonId) : [...linkedAddons, addonId]);
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-success/10 dark:bg-emerald-900/10 rounded-lg">
        <p className="text-xs text-success dark:text-emerald-400">
          Select add-ons from the central library. Toggle branch availability for each.
          <a href="/addons" className="underline ml-1" target="_blank">Manage Library</a>
        </p>
      </div>
      {available.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">No add-ons in library. <a href="/addons" className="text-orange-500 underline">Create some first</a>.</p>
      )}
      {available.map(addon => {
        const isLinked = linkedAddons.includes(addon.id);
        return (
          <div key={addon.id} className={`border rounded-lg p-3 space-y-2 transition-colors ${isLinked ? 'bg-success/10 border-success/30 dark:bg-emerald-900/10' : ''}`}>
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggle(addon.id)}>
              <Checkbox checked={isLinked} onCheckedChange={() => toggle(addon.id)} />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{addon.name}</span>
                {addon.name_ar && <span className="text-xs text-muted-foreground ml-2" dir="rtl">{addon.name_ar}</span>}
              </div>
              <Badge variant="outline" className="text-[10px]">{addon.category}</Badge>
              <span className="text-sm font-semibold text-orange-600">{cur()} {addon.price}</span>
            </div>
            {isLinked && branches.length > 0 && (
              <div className="pl-8">
                <p className="text-[11px] text-muted-foreground mb-1">Available at:</p>
                <div className="flex flex-wrap gap-1">
                  {branches.map(b => {
                    const avail = branchAvailability[b.id] || [];
                    const isOn = avail.includes(addon.id);
                    return (
                      <Badge key={b.id} variant={isOn ? 'default' : 'outline'}
                        className={`text-[10px] cursor-pointer select-none ${isOn ? 'bg-orange-500' : ''}`}
                        onClick={() => {
                          const next = { ...branchAvailability };
                          const curr = next[b.id] || [];
                          next[b.id] = isOn ? curr.filter(s => s !== addon.id) : [...curr, addon.id];
                          onAvailChange(next);
                        }}
                        data-testid={`addon-branch-${b.id}-${addon.id}`}
                      >{b.name}</Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OptionGroupsEditor({ groups, onChange, branches }) {
  const addGroup = () => onChange([...groups, { name: '', required: false, options: [{ name: '', price: 0 }], branch_availability: {} }]);
  const removeGroup = (i) => onChange(groups.filter((_, j) => j !== i));
  const updateGroup = (i, field, val) => {
    const next = [...groups];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const addOption = (gi) => {
    const next = [...groups];
    next[gi].options = [...next[gi].options, { name: '', price: 0 }];
    onChange(next);
  };
  const removeOption = (gi, oi) => {
    const next = [...groups];
    next[gi].options = next[gi].options.filter((_, j) => j !== oi);
    onChange(next);
  };
  const updateOption = (gi, oi, field, val) => {
    const next = [...groups];
    const opts = [...next[gi].options];
    opts[oi] = { ...opts[oi], [field]: field === 'price' ? (parseFloat(val) || 0) : val };
    next[gi].options = opts;
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
        <p className="text-xs text-purple-700 dark:text-purple-400">
          Create single-choice modifier groups (e.g., &quot;Bread Type&quot;: Pita / Saj). Customer picks one option.
        </p>
      </div>
      {groups.map((g, gi) => (
        <div key={gi} className="border rounded-lg p-3 space-y-3">
          <div className="flex items-center gap-3">
            <Input placeholder="Group name (e.g., Bread Type)" value={g.name} onChange={e => updateGroup(gi, 'name', e.target.value)} className="flex-1" data-testid={`optgroup-name-${gi}`} />
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Required</Label>
              <Switch checked={g.required} onCheckedChange={v => updateGroup(gi, 'required', v)} />
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-error" onClick={() => removeGroup(gi)}><Trash2 size={14} /></Button>
          </div>
          <div className="pl-2 space-y-2">
            {g.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <Input placeholder="Option name" value={opt.name} onChange={e => updateOption(gi, oi, 'name', e.target.value)} className="flex-1" data-testid={`optgroup-opt-name-${gi}-${oi}`} />
                <Input type="number" step="0.01" placeholder="Extra price" value={opt.price} onChange={e => updateOption(gi, oi, 'price', e.target.value)} className="w-24" data-testid={`optgroup-opt-price-${gi}-${oi}`} />
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removeOption(gi, oi)}><X size={12} /></Button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => addOption(gi)} data-testid={`add-option-${gi}`}><Plus size={12} className="mr-1" />Add Option</Button>
          </div>
          {branches.length > 0 && (
            <div className="pl-2">
              <p className="text-[11px] text-muted-foreground mb-1">Available at branches:</p>
              <div className="flex flex-wrap gap-1">
                {branches.map(b => {
                  const avail = g.branch_availability || {};
                  const isOn = avail[b.id] === true;
                  return (
                    <Badge key={b.id} variant={isOn ? 'default' : 'outline'}
                      className={`text-[10px] cursor-pointer select-none ${isOn ? 'bg-orange-500' : ''}`}
                      onClick={() => {
                        const next = { ...(g.branch_availability || {}) };
                        next[b.id] = !isOn;
                        updateGroup(gi, 'branch_availability', next);
                      }}
                    >{b.name}</Badge>
                  );
                })}
              </div>
              {Object.keys(g.branch_availability || {}).filter(k => g.branch_availability[k]).length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">No branches selected = available at all branches</p>
              )}
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" className="w-full" onClick={addGroup} data-testid="add-optgroup-btn"><Plus size={16} className="mr-1" /> Add Option Group</Button>
    </div>
  );
}

// ---- Main Component ----

export default function MenuItemsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branches, fetchBranches } = useBranchStore();
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [centralAddons, setCentralAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkBranches, setBulkBranches] = useState([]);
  const [bulkPlatforms, setBulkPlatforms] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [exportPlatformId, setExportPlatformId] = useState('');
  // When deep-linked from the POS Performance overlay's "Suggest new
  // price" button (`?edit=<id>&suggest_price=<n>`), this banner shows
  // inside the edit dialog with a one-click "Apply" action.
  const [suggestedPriceBanner, setSuggestedPriceBanner] = useState(null);
  // Sales Movers — menu items whose 7-day sales count grew or shrank
  // most vs the prior 7 days. Fetched lazily on mount; tolerant of
  // backend errors so the page still loads if the endpoint isn't
  // available yet (e.g. older backend version, empty pos_orders).
  const [salesMovers, setSalesMovers] = useState([]);
  const fileInputRef = useRef(null);
  const uploadItemRef = useRef(null);

  // Dynamic categories
  const [customCategories, setCustomCategories] = useState([]);
  const [posCategoryList, setPosCategoryList] = useState([]); // iter379: covers per category
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  // Menu Phase 2 — cost alerts, branch cost view, bulk recipe link
  const [costAlerts, setCostAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [showBranchCost, setShowBranchCost] = useState(false);
  const [branchCostData, setBranchCostData] = useState(null);
  const [belowOnly, setBelowOnly] = useState(true);
  const [linkRecipeOpen, setLinkRecipeOpen] = useState(false);
  const [linkRecipeId, setLinkRecipeId] = useState('');
  const [linkScale, setLinkScale] = useState('1');
  const [linking, setLinking] = useState(false);
  const [marginTarget, setMarginTarget] = useState(0.30);
  const [fixPreview, setFixPreview] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);  // iter879
  const [newCategoryName, setNewCategoryName] = useState('');
  // iter877 — branch chips for scoping newly-created categories. Empty
  // list = "all branches" (backend default).
  const [newCategoryBranchIds, setNewCategoryBranchIds] = useState([]);
  const [savingCategory, setSavingCategory] = useState(false);

  const CATEGORIES = [
    ...DEFAULT_CATEGORIES,
    ...customCategories.map(c => ({ id: c.name.toLowerCase().replace(/\s+/g, '_'), name: c.name, dbId: c.id }))
  ];

  // V2 form state
  const emptyForm = {
    name: '', name_ar: '', description: '', category: 'main',
    price: '', cost_price: '', cost_price_auto: true, preparation_time: '10',
    is_available: true, tags: [], branch_ids: [], platform_ids: [], platform_prices: {},
    // iter845i — Self-Service Online Ordering: per-item availability
    // toggle. Independent of `is_available` (which gates the in-store
    // POS). Default true so existing items stay online-visible.
    available_online: true,
    sizes: [], sizesBranchAvail: {},
    has_sizes: false,  // iter877 — master toggle for size variations
    linkedAddonIds: [], addonsBranchAvail: {},
    optionGroups: [],
    branch_prices: {},
    // iter597 — Per-item VAT inclusivity. Default false so existing
    // items behave exactly as before.
    price_includes_tax: false,
    schedule: { enabled: false, available_days: [0,1,2,3,4,5,6], start_time: '00:00', end_time: '23:59', unavailable_behavior: 'disabled' },
    // iter635 — Foodics-style additions: allergens multi-select.
    allergens: [],
    allergens_reviewed: false,  // Menu Phase 2 — compliance flag
    // Recipe & Production Phase 1 — stock-driven recipe links (per size scaling).
    recipe_links: [],
  };
  const [formData, setFormData] = useState(emptyForm);
  const [recipes, setRecipes] = useState([]);
  const [allergenChoices, setAllergenChoices] = useState([]);
  const [statusTab, setStatusTab] = useState('all');  // all|active|inactive
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  // iter638 — Foodics-style table view as the primary layout. Card
  // grid stays available behind a toolbar toggle; we persist the
  // preference in localStorage so a user's pick survives navigation.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('menu-items-view') || 'list'; }
    catch { return 'list'; }
  });
  useEffect(() => {
    try { localStorage.setItem('menu-items-view', viewMode); } catch { /* localStorage unavailable */ }
  }, [viewMode]);
  // iter639 — Inline price editor state. Tracks which row is being
  // edited and the in-flight draft value.
  const [priceEdit, setPriceEdit] = useState({ id: null, value: '', saving: false });
  const savePriceEdit = async () => {
    if (!priceEdit.id) return;
    const raw = String(priceEdit.value || '').trim();
    if (raw === '') { setPriceEdit({ id: null, value: '', saving: false }); return; }
    const num = parseFloat(raw);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Price must be a non-negative number');
      return;
    }
    const target = items.find(it => it.id === priceEdit.id);
    if (target && Number(target.price) === num) {
      setPriceEdit({ id: null, value: '', saving: false });
      return;
    }
    setPriceEdit(p => ({ ...p, saving: true }));
    try {
      const { data } = await api.patch(`/cashier/menu/${priceEdit.id}/price`, { price: num });
      setItems(prev => prev.map(it => it.id === priceEdit.id ? { ...it, price: data.price } : it));
      toast.success(`Price updated to ${cur()} ${Number(data.price).toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update price');
    } finally {
      setPriceEdit({ id: null, value: '', saving: false });
    }
  };
  // iter643 — Inline category change + availability toggle.
  const changeCategory = async (item, newCategory) => {
    if (!newCategory || newCategory === item.category) return;
    try {
      const { data } = await api.patch(`/cashier/menu/${item.id}/category`, { category: newCategory });
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, category: data.category } : it));
      toast.success('Category updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change category');
    }
  };
  const toggleAvailability = async (item) => {
    const next = !item.is_available;
    // Optimistic update
    setItems(prev => prev.map(it => it.id === item.id ? { ...it, is_available: next } : it));
    try {
      await api.patch(`/cashier/menu/${item.id}/availability`, { is_available: next });
      toast.success(next ? 'Item activated' : 'Item deactivated');
    } catch (err) {
      // Roll back
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, is_available: !next } : it));
      toast.error(err.response?.data?.detail || 'Failed to toggle availability');
    }
  };

  // iter645 — Bulk-edit toolbar handlers.
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkApplyDiscount = async (percent) => {
    if (!selectedItems.length || Number.isNaN(percent)) return;
    if (!window.confirm(`Apply ${percent}% ${percent >= 0 ? 'discount' : 'price hike'} to ${selectedItems.length} item(s)?`)) return;
    setBulkSaving(true);
    try {
      const { data } = await api.put('/cashier/menu/bulk-discount', {
        item_ids: selectedItems, percent,
      });
      // Update local prices from response
      const byId = Object.fromEntries((data.items || []).map(u => [u.id, u.new]));
      setItems(prev => prev.map(it => it.id in byId ? { ...it, price: byId[it.id] } : it));
      toast.success(`Updated price on ${data.updated} item(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk discount failed');
    } finally {
      setBulkSaving(false);
    }
  };
  const bulkMoveCategory = async (newCategory) => {
    if (!selectedItems.length || !newCategory) return;
    setBulkSaving(true);
    try {
      const { data } = await api.put('/cashier/menu/bulk-category', {
        item_ids: selectedItems, category: newCategory,
      });
      setItems(prev => prev.map(it => selectedItems.includes(it.id) ? { ...it, category: newCategory } : it));
      toast.success(`Moved ${data.updated} item(s) to ${newCategory}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk move failed');
    } finally {
      setBulkSaving(false);
    }
  };
  const bulkSetAvailability = async (isAvailable) => {
    if (!selectedItems.length) return;
    setBulkSaving(true);
    try {
      const { data } = await api.put('/cashier/menu/bulk-availability', {
        item_ids: selectedItems, is_available: isAvailable,
      });
      setItems(prev => prev.map(it => selectedItems.includes(it.id) ? { ...it, is_available: isAvailable } : it));
      toast.success(`${isAvailable ? 'Activated' : 'Deactivated'} ${data.updated} item(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkSaving(false);
    }
  };
  const bulkDelete = async () => {
    if (!selectedItems.length) return;
    if (!window.confirm(`Permanently delete ${selectedItems.length} item(s)? This cannot be undone.`)) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post('/cashier/menu/bulk-delete', { item_ids: selectedItems });
      setItems(prev => prev.filter(it => !selectedItems.includes(it.id)));
      setSelectedItems([]);
      toast.success(`Deleted ${data.deleted} item(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkSaving(false);
    }
  };
  useEffect(() => {
    api.get('/allergens')
      .then(r => setAllergenChoices((r.data.allergens || []).filter(a => !a.deleted)))
      .catch(() => {});
  }, []);

  const fetchCategories = async () => {
    try { const { data } = await api.get('/categories?category_type=menu'); setCustomCategories(data || []); } catch { /* fallback to defaults */ }
    // iter379: also fetch the POS-level category list which now carries
    // per-category cover images (manual override OR auto-derived from
    // the top item). We use it to render tiny cover thumbnails next to
    // each row in the Manage Categories dialog.
    try { const { data } = await api.get('/cashier/categories'); setPosCategoryList(data || []); } catch { /* fallback to empty list */ }
  };
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSavingCategory(true);
    try {
      await api.post('/categories', {
        name: newCategoryName.trim(),
        type: 'menu',
        branch_ids: newCategoryBranchIds,  // iter877
      });
      toast.success(`Category "${newCategoryName.trim()}" added${newCategoryBranchIds.length ? ` for ${newCategoryBranchIds.length} branch(es)` : ' (all branches)'}`);
      const catId = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_');
      const catName = newCategoryName.trim();
      setNewCategoryName(''); setNewCategoryBranchIds([]); fetchCategories();
      generateCategoryCover(catId, catName, { silent: true });  // AI cover in background
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingCategory(false); }
  };
  const deleteCategory = async (cat) => {
    if (!cat.dbId) return;
    try { await api.delete(`/categories/${cat.dbId}`); toast.success(`Category "${cat.name}" removed`); fetchCategories(); }
    catch { toast.error('Failed to delete category'); }
  };

  // Menu Phase 2 — cost alerts, branch cost view & bulk recipe link handlers.
  const fetchCostAlerts = () => api.get('/menu-cost-alerts').then(r => { setCostAlerts(r.data?.alerts || []); if (r.data?.target) setMarginTarget(r.data.target); }).catch(() => {});
  useEffect(() => { fetchCostAlerts(); }, []);
  // "Fix all" deep-link from the Daily Cost Alerts email — pre-select flagged items.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cost_alerts') === '1') {
      setAlertsOpen(true);
      api.get('/menu-cost-alerts').then(r => {
        const ids = (r.data?.alerts || []).map(a => a.item_id);
        if (ids.length) { setSelectedItems(ids); toast.info(`${ids.length} flagged item(s) pre-selected — use Fix Margin or bulk actions`); }
      }).catch(() => {});
    }
  }, []);
  const updateMarginTarget = async (val) => {
    const t = parseFloat(val);
    setMarginTarget(t);
    try { await api.put('/menu-settings/margin-target', { margin_target: t }); toast.success(`Margin target set to ${Math.round(t * 100)}%`); fetchCostAlerts(); if (showBranchCost) openBranchCost(); }
    catch { toast.error('Failed to save target'); }
  };
  const openHistory = async () => {
    setShowHistory(true); setHistoryData(null);
    try { const { data } = await api.get('/menu-cost-alerts/history'); setHistoryData(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load history'); }
  };
  const openFixPreview = (alert) => {
    const cost = Number(alert.new_cost) || 0;
    const newPrice = cost > 0 ? +(cost / (1 - marginTarget)).toFixed(2) : 0;
    setFixPreview({ alert, newPrice });
  };
  const confirmFixMargin = async () => {
    const alert = fixPreview?.alert; if (!alert) return;
    try {
      const { data } = await api.post(`/cashier/menu/${alert.item_id}/fix-margin`, {});
      toast.success(`${alert.name}: price set to SR ${data.new_price} (${Math.round(data.target * 100)}% margin, all branches)`);
      setFixPreview(null); fetchItems(); fetchCostAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not fix margin'); }
  };
  const [fixAllOpen, setFixAllOpen] = useState(false);
  const fixAllRows = () => costAlerts.map(a => {
    const cost = Number(a.new_cost) || 0;
    const t = a.target || marginTarget;
    return { ...a, newPrice: cost > 0 ? +(cost / (1 - t)).toFixed(2) : 0, t };
  });
  const confirmFixAll = async () => {
    try {
      const { data } = await api.post('/cashier/menu/fix-all-margins', {});
      toast.success(`Repriced ${data.repriced} item(s) to target margin across all branches`);
      setFixAllOpen(false); fetchItems(); fetchCostAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fix all failed'); }
  };
  const exportHistory = async () => {
    try {
      const res = await api.get('/menu-cost-alerts/history/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = `cost_alert_history_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };
  const [byCategory, setByCategory] = useState({});
  const [catTargetCat, setCatTargetCat] = useState('');
  const fetchTargetSettings = () => api.get('/menu-settings/margin-target').then(r => { if (r.data?.target || r.data?.margin_target) setMarginTarget(r.data.margin_target || r.data.target); setByCategory(r.data?.by_category || {}); }).catch(() => {});
  const setCategoryTarget = async (category, val) => {
    if (!category) return;
    try {
      await api.put('/menu-settings/margin-target', { category, margin_target: parseFloat(val) });
      setByCategory(prev => ({ ...prev, [category]: parseFloat(val) }));
      toast.success(`${category} target set to ${Math.round(parseFloat(val) * 100)}%`);
      if (showBranchCost) openBranchCost();
    } catch { toast.error('Failed to set category target'); }
  };
  const dismissAlerts = async () => {
    try { await api.post('/menu-cost-alerts/dismiss', {}); setCostAlerts([]); setAlertsOpen(false); toast.success('Cost alerts dismissed'); }
    catch { toast.error('Failed to dismiss'); }
  };
  const openBranchCost = async () => {
    setShowBranchCost(true); setBranchCostData(null);
    fetchTargetSettings();
    try { const { data } = await api.get('/cashier/branch-cost-report'); setBranchCostData(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
  };
  const doBulkRecipeLink = async () => {
    if (!linkRecipeId) { toast.error('Pick a recipe'); return; }
    setLinking(true);
    try {
      const { data } = await api.post('/cashier/menu/bulk-recipe-link', { item_ids: selectedItems, recipe_id: linkRecipeId, scale: parseFloat(linkScale) || 1 });
      toast.success(`Linked "${data.recipe_name}" to ${data.updated} item(s)${data.cost_applied != null ? ` · cost SR ${data.cost_applied}` : ''}`);
      setLinkRecipeOpen(false); setLinkRecipeId(''); setSelectedItems([]); fetchItems(); fetchCostAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
    finally { setLinking(false); }
  };
  const bulkSetAutoCost = async (enabled) => {
    if (selectedItems.length === 0) return;
    try {
      const { data } = await api.put('/cashier/menu/bulk-auto-cost', { item_ids: selectedItems, enabled });
      toast.success(`Auto-cost ${enabled ? 'ON' : 'OFF'} for ${data.updated} item(s)${enabled && data.cost_applied ? ` · ${data.cost_applied} costed from recipe` : ''}`);
      setSelectedItems([]); fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter379 — category cover image helpers.
  const uploadCategoryCover = async (catId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/cashier/categories/${catId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Cover uploaded');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload cover'); }
  };
  const removeCategoryCover = async (catId) => {
    try {
      await api.delete(`/cashier/categories/${catId}/image`);
      toast.success('Manual cover removed — using auto-derived cover');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const toBackendImageUrl = (u) => {
    if (!u) return null;
    let url = u;
    if (url.startsWith('/uploads/')) url = '/api' + url;
    if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
    return url;
  };

  useEffect(() => {
    fetchBranches();
    fetchItems();
    fetchCategories();
    api.get('/platforms').then(r => setPlatforms(r.data || [])).catch(() => {});
    api.get('/addons').then(r => setCentralAddons(r.data || [])).catch(() => {});
    api.get('/recipes').then(r => setRecipes(r.data || [])).catch(() => {});    // Sales Movers — silently degrade if endpoint missing or no data.
    api.get('/menu-analytics/sales-movers?window_days=7&limit=3')
      .then(r => setSalesMovers(r.data?.movers || []))
      .catch(() => setSalesMovers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try { const { data } = await api.get('/cashier/menu-all'); setItems(data); }
    catch { toast.error('Failed to fetch menu items'); }
    finally { setLoading(false); }
  };

  // Deep-link handler — when the user arrives via
  // /menu-items?edit=<id>&suggest_price=<n> (from the POS Performance
  // overlay's "Suggest new price" button), we wait for the menu list to
  // load, find the matching item, open its editor, and surface a banner
  // with the suggested minimum-profit price + one-click "Apply" button.
  useEffect(() => {
    const editId = searchParams.get('edit');
    const suggestPrice = searchParams.get('suggest_price');
    if (!editId || !items.length) return;
    const target = items.find(i => i.id === editId);
    if (!target) {
      toast.error('Item not found — it may have been deleted');
      // Clear the param so refresh doesn't re-trigger
      const next = new URLSearchParams(searchParams); next.delete('edit'); next.delete('suggest_price');
      setSearchParams(next, { replace: true });
      return;
    }
    if (suggestPrice) {
      setSuggestedPriceBanner({
        itemId: editId,
        itemName: target.name,
        currentPrice: target.price || 0,
        suggestedPrice: parseFloat(suggestPrice) || 0,
        cost: target.cost_price || 0,
      });
    }
    handleEdit(target);
    // Strip the query so refresh inside the dialog won't re-pop it
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('suggest_price');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Margin Badge — resolve a menu item's live recipe cost from its default
  // recipe link (size_name '') × scale, reusing the recipes' auto-computed
  // cost_per_unit. Returns null when there's no linked recipe.
  const recipeById = Object.fromEntries((recipes || []).map(r => [r.id, r]));
  const getRecipeCost = (item) => {
    const links = item.recipe_links || [];
    if (!links.length) return null;
    const def = links.find(l => !(l.size_name)) || links[0];
    const r = recipeById[def.recipe_id];
    if (!r) return null;
    return Number(r.cost_per_unit || 0) * Number(def.scale || 1);
  };

  const filteredItems = items.filter(item => {
    // iter635 — Foodics-style status tabs.
    if (statusTab === 'active' && item.is_available === false) return false;
    if (statusTab === 'inactive' && item.is_available !== false) return false;
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
    if (branchFilter !== 'all') {
      const bIds = item.branch_ids || [];
      if (bIds.length > 0 && !bIds.includes(branchFilter)) return false;
    }
    if (platformFilter !== 'all') {
      const pIds = item.platform_ids || [];
      if (!pIds.includes(platformFilter)) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.name_ar?.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q);
    }
    return true;
  });

  // Reset to first page whenever the result set changes (filters/search/size).
  useEffect(() => { setPage(0); }, [searchQuery, categoryFilter, branchFilter, platformFilter, statusTab, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedItems = filteredItems.slice(safePage * pageSize, (safePage + 1) * pageSize);

  // Inline "Add new category" from the edit sheet — reuses the same
  // /categories module as the Manage Menu Categories dialog. Returns the
  // CATEGORIES id so the caller can select it immediately.
  const createCategoryInline = async (name) => {
    const n = (name || '').trim();
    if (!n) return null;
    await api.post('/categories', { name: n, type: 'menu', branch_ids: [] });
    await fetchCategories();
    toast.success(`Category "${n}" added`);
    const id = n.toLowerCase().replace(/\s+/g, '_');
    generateCategoryCover(id, n, { silent: true });  // fire-and-forget AI cover
    return id;
  };

  // Category Images — AI-generate a cover tile so a brand-new/empty category
  // looks complete in the POS grid immediately. Reuses the category_covers
  // storage keyed by the same tile id as manual uploads.
  const [coverBusy, setCoverBusy] = useState('');
  const generateCategoryCover = async (catId, name, { silent = false } = {}) => {
    setCoverBusy(catId);
    if (!silent) toast.info('Generating AI cover…');
    else toast.message(`Generating an AI cover for "${name}"…`);
    try {
      await api.post('/menu-ai/category-cover', { category_id: catId, name });
      await fetchCategories();
      toast.success(`AI cover ready for "${name}"`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Cover generation failed'); }
    finally { setCoverBusy(''); }
  };

  // Bulk AI Fill — fill missing Arabic name / description / allergens across
  // the selected items in one server-side pass.
  const [aiFilling, setAiFilling] = useState(false);
  const bulkAiFill = async () => {
    if (selectedItems.length === 0) return;
    setAiFilling(true);
    toast.info(`AI is filling missing fields for ${selectedItems.length} item(s)…`);
    try {
      const { data } = await api.post('/menu-ai/bulk-fill', { item_ids: selectedItems });
      const f = data.filled || {};
      toast.success(`Updated ${data.updated} item(s) — AR ${f.name_ar || 0}, descriptions ${f.description || 0}, allergens ${f.allergens || 0}`);
      setSelectedItems([]);
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk AI fill failed'); }
    finally { setAiFilling(false); }
  };

  // Parse V2 modifier_groups back into form state for editing
  const parseModifierGroups = (item) => {
    const mgs = item.modifier_groups || [];
    const sizes = [];
    let sizesBranchAvail = {};
    const linkedAddonIds = [];
    let addonsBranchAvail = {};
    const optionGroups = [];

    // V2 groups
    for (const mg of mgs) {
      if (mg.type === 'size') {
        for (const opt of (mg.options || [])) sizes.push({ name: opt.name, price: opt.price || 0 });
        sizesBranchAvail = mg.branch_availability || {};
      } else if (mg.type === 'addon') {
        linkedAddonIds.push(...(mg.addon_ids || []));
        addonsBranchAvail = mg.branch_availability || {};
      } else if (mg.type === 'option') {
        optionGroups.push({
          name: mg.name, required: mg.required || false,
          options: mg.options || [], branch_availability: mg.branch_availability || {}
        });
      }
    }

    // Fallback: parse legacy V1 modifiers if no V2 groups exist
    if (mgs.length === 0 && (item.modifiers || []).length > 0) {
      for (const mod of item.modifiers) {
        if (mod.name === 'Size') {
          for (const opt of (mod.options || [])) sizes.push({ name: opt.name, price: opt.price || 0 });
        } else if (mod.name === 'Add-ons') {
          // V1 add-ons were inline, can't link to library - skip
        } else {
          optionGroups.push({
            name: mod.name, required: mod.required || false,
            options: mod.options || [], branch_availability: {}
          });
        }
      }
    }

    return { sizes, sizesBranchAvail, linkedAddonIds, addonsBranchAvail, optionGroups };
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const parsed = parseModifierGroups(item);
    setFormData({
      name: item.name || '', name_ar: item.name_ar || '', description: item.description || '',
      category: item.category || 'main', price: item.price?.toString() || '',
      cost_price: item.cost_price?.toString() || '', cost_price_auto: item.cost_price_auto !== false, preparation_time: item.preparation_time?.toString() || '10',
      is_available: item.is_available !== false, tags: item.tags || [],
      available_online: item.available_online !== false,  // iter845i
      branch_ids: item.branch_ids || [], platform_ids: item.platform_ids || [],
      platform_prices: item.platform_prices || {},
      ...parsed,
      branch_prices: item.branch_prices || {},
      has_sizes: item.has_sizes ?? ((item.sizes || parsed.sizes || []).length > 0),  // iter877
      price_includes_tax: !!item.price_includes_tax,  // iter597
      schedule: item.schedule || { enabled: false, available_days: [0,1,2,3,4,5,6], start_time: '00:00', end_time: '23:59', unavailable_behavior: 'disabled' },
      allergens: item.allergens || [],  // iter635
      allergens_reviewed: !!item.allergens_reviewed,
      recipe_links: item.recipe_links || [],
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) { toast.error('Name and price are required'); return; }
    setSaving(true);
    try {
      // Build V2 modifier_groups
      const modifier_groups = [];
      const validSizes = formData.sizes.filter(s => s.name);
      if (validSizes.length > 0) {
        modifier_groups.push({
          id: 'sizes', name: 'Size', type: 'size', required: true, multiple: false,
          options: validSizes.map(s => ({ name: s.name, price: s.price || 0 })),
          branch_availability: formData.sizesBranchAvail || {}
        });
      }
      if (formData.linkedAddonIds.length > 0) {
        modifier_groups.push({
          id: 'addons', name: 'Add-ons', type: 'addon', required: false, multiple: true,
          addon_ids: formData.linkedAddonIds,
          branch_availability: formData.addonsBranchAvail || {}
        });
      }
      for (const og of formData.optionGroups) {
        if (og.name && og.options.some(o => o.name)) {
          modifier_groups.push({
            id: og.name.toLowerCase().replace(/\s+/g, '_'),
            name: og.name, type: 'option', required: og.required || false, multiple: false,
            options: og.options.filter(o => o.name),
            branch_availability: og.branch_availability || {}
          });
        }
      }

      // Also build legacy modifiers for backward compat with POS orders
      const modifiers = [];
      if (validSizes.length > 0) {
        modifiers.push({ name: 'Size', required: true, multiple: false, options: validSizes });
      }
      // Resolve linked addons to inline options for legacy compat
      if (formData.linkedAddonIds.length > 0) {
        const addonOpts = formData.linkedAddonIds.map(id => {
          const a = centralAddons.find(x => x.id === id);
          return a ? { name: a.name, price: a.price, addon_id: a.id } : null;
        }).filter(Boolean);
        if (addonOpts.length > 0) modifiers.push({ name: 'Add-ons', required: false, multiple: true, options: addonOpts });
      }
      for (const og of formData.optionGroups) {
        if (og.name && og.options.some(o => o.name)) {
          modifiers.push({ name: og.name, required: og.required, multiple: false, options: og.options.filter(o => o.name) });
        }
      }

      const payload = {
        name: formData.name, name_ar: formData.name_ar, description: formData.description,
        category: formData.category, price: parseFloat(formData.price),
        cost_price: parseFloat(formData.cost_price) || 0,
        cost_price_auto: formData.cost_price_auto !== false,
        preparation_time: parseInt(formData.preparation_time) || 10,
        is_available: formData.is_available, tags: formData.tags,
        available_online: formData.available_online !== false,  // iter845i
        branch_ids: formData.branch_ids, platform_ids: formData.platform_ids,
        platform_prices: formData.platform_prices, branch_prices: formData.branch_prices || {},
        modifier_groups, modifiers,
        price_includes_tax: !!formData.price_includes_tax,  // iter597
        // iter877 — has_sizes toggle + size variations. When off, sizes will
        // be cleared server-side (see cashier_pos.create_menu_item).
        has_sizes: !!formData.has_sizes,
        sizes: formData.has_sizes ? (formData.sizes || []) : [],
        schedule: formData.schedule?.enabled ? formData.schedule : null,
        allergens: formData.allergens || [],  // iter635
        allergens_reviewed: !!formData.allergens_reviewed,
        recipe_links: (formData.recipe_links || []).filter(l => l.recipe_id),
      };

      if (editingItem) {
        await api.put(`/cashier/menu/${editingItem.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/cashier/menu', payload);
        toast.success('Item created');
      }
      setShowDialog(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    try { await api.delete(`/cashier/menu/${item.id}`); toast.success('Item deleted'); fetchItems(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!confirm(`Permanently delete ${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post('/cashier/menu/bulk-delete', { item_ids: selectedItems });
      toast.success(`Deleted ${data?.deleted ?? selectedItems.length} item${selectedItems.length === 1 ? '' : 's'}`);
      setSelectedItems([]);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete items'); }
  };

  const handleImageUpload = async (item, file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try { await api.post(`/cashier/menu/${item.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Image uploaded'); fetchItems(); }
    catch { toast.error('Failed to upload'); }
    finally { setUploading(false); }
  };

  const toggleSelect = (id) => setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const selectAll = () => {
    if (selectedItems.length === filteredItems.length) setSelectedItems([]);
    else setSelectedItems(filteredItems.map(i => i.id));
  };

  const handleBulkAssign = async () => {
    try {
      if (bulkBranches.length > 0) await api.put('/cashier/menu/bulk-branch-assign', { item_ids: selectedItems, branch_ids: bulkBranches });
      if (bulkPlatforms.length > 0) await api.put('/cashier/menu/bulk-platform-assign', { item_ids: selectedItems, platform_ids: bulkPlatforms });
      toast.success(`Updated ${selectedItems.length} items`);
      setShowBulkAssign(false); setSelectedItems([]); fetchItems();
    } catch { toast.error('Failed to update'); }
  };

  const handleExport = async () => {
    if (!exportPlatformId) return;
    try {
      const { data } = await api.get(`/cashier/menu/export/${exportPlatformId}`);
      const text = data.items.map((item, i) => `${i+1}. ${item.name} | ${item.name_ar} | ${item.category} | ${cur()} ${item.price} | ${item.preparation_time}min`).join('\n');
      const blob = new Blob([`Menu for ${data.platform} (${data.total_items} items)\n${'='.repeat(60)}\n\n${text}`], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `menu_${data.platform.replace(/\s/g,'_')}.txt`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total_items} items`); setShowExport(false);
    } catch { toast.error('Failed to export'); }
  };

  const toggleBranch = (branchId) => setFormData(prev => ({ ...prev, branch_ids: prev.branch_ids.includes(branchId) ? prev.branch_ids.filter(b => b !== branchId) : [...prev.branch_ids, branchId] }));
  const togglePlatform = (platformId) => setFormData(prev => ({ ...prev, platform_ids: prev.platform_ids.includes(platformId) ? prev.platform_ids.filter(p => p !== platformId) : [...prev.platform_ids, platformId] }));

  const getImageUrl = (item) => {
    if (!item.image_url) return null;
    // iter379: upgrade legacy `/uploads/...` paths to `/api/uploads/...`
    // so they route through the K8s ingress to the backend static mount.
    let url = item.image_url;
    if (url.startsWith('/uploads/')) url = '/api' + url;
    if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
    return url;
  };
  const getBranchName = (id) => branches.find(b => b.id === id)?.name || id;
  const getPlatformName = (id) => platforms.find(p => p.id === id)?.name || id;

  // Count modifier groups for display
  const getModifierCount = (item) => {
    const v2 = (item.modifier_groups || []).length;
    if (v2 > 0) return v2;
    return (item.modifiers || []).length;
  };

  const getModifierLabels = (item) => {
    const mgs = item.modifier_groups || [];
    if (mgs.length > 0) return mgs.map(mg => ({ name: mg.name, count: mg.type === 'addon' ? (mg.addon_ids || []).length : (mg.options || []).length }));
    return (item.modifiers || []).map(m => ({ name: m.name, count: m.options?.length || 0 }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="menu-items-page">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="menu-items"
          title="What is the Menu Management module?"
          tone="emerald"
        >
          <p>
            Define every item your branches sell — name, price, cost, sizes, add-ons, and per-platform pricing
            (Talabat, HungerStation, etc.). Use <strong>Bulk import</strong> to onboard hundreds of items from
            an Excel/CSV in one go, or <strong>Bulk assign</strong> to push the same item to multiple branches /
            platforms with one click. The <strong>Sales Movers</strong> panel below highlights which items are
            gaining or losing share over the last 7 days so you know what to feature, restock, or retire.
          </p>
        </PageInfoCallout>

        {/* Sales Movers panel — mirrors the Top Movers widget on
            /banking-rules. Hidden when there's no movement to show
            (fresh tenant, no orders yet, or all items perfectly flat). */}
        {salesMovers.length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50/30 dark:bg-emerald-950/10" data-testid="menu-sales-movers">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-outfit flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                <TrendingUp size={13} /> Sales movers · last 7 days vs prior 7 days
                <span className="text-[10px] font-normal text-muted-foreground ml-1" title="Items whose sales volume changed the most">
                  · spot trending and slipping items
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {salesMovers.map((m) => {
                  const Icon = m.direction === 'up' ? TrendingUp : TrendingDown;
                  const tone = m.direction === 'up'
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-rose-300 bg-rose-50 text-rose-800';
                  return (
                    <div key={m.item_id} className={`rounded-lg border px-2.5 py-2 ${tone}`} data-testid={`menu-mover-${m.item_id}`}>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold leading-tight">
                        <Icon size={12} className="shrink-0" />
                        <span className="truncate" title={m.name}>{m.name}</span>
                      </div>
                      <div className="mt-1 text-[10px] tabular-nums opacity-90">
                        {m.previous_qty} → <strong>{m.current_qty}</strong> sold
                        <span className="ml-1.5 font-semibold">
                          {m.delta > 0 ? '+' : ''}{m.delta}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <UtensilsCrossed size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="menuitems-title">Menu Management</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Menu items and product catalog</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm">Manage items, sizes, add-ons & branch availability</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* iter831 — Always-visible bulk shortcuts: apply/remove VAT
                and enable/disable across the entire menu with one tap.
                Selection-dependent bulk actions remain below. */}
            <Button
              variant="outline"
              className="rounded-xl border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-300"
              onClick={async () => {
                if (!window.confirm('Mark ALL menu items as VAT-inclusive (price INCLUDES VAT)? This overrides any per-item setting.')) return;
                try {
                  const { data } = await api.put('/cashier/menu/bulk-set-vat-inclusive', { all: true, value: true });
                  toast.success(`${data?.updated ?? 0} items now include VAT`);
                  fetchItems();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
              }}
              data-testid="bulk-vat-all-inclusive-btn"
              title="Mark every item's price as VAT-inclusive"
            >
              <DollarSign size={15} className="mr-1.5" />VAT: All ✓
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 dark:bg-slate-950/20 dark:border-slate-800 dark:text-slate-300"
              onClick={async () => {
                if (!window.confirm('Remove VAT-inclusive flag from ALL menu items (price EXCLUDES VAT)?')) return;
                try {
                  const { data } = await api.put('/cashier/menu/bulk-set-vat-inclusive', { all: true, value: false });
                  toast.success(`${data?.updated ?? 0} items now exclude VAT`);
                  fetchItems();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
              }}
              data-testid="bulk-vat-all-exclusive-btn"
              title="Remove 'Price includes VAT' from every item"
            >
              <DollarSign size={15} className="mr-1.5" />VAT: None
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-300"
              onClick={async () => {
                if (!window.confirm('Activate ALL menu items? Currently disabled items will become available for sale.')) return;
                try {
                  const { data } = await api.put('/cashier/menu/bulk-availability', { all: true, is_available: true });
                  toast.success(`${data?.updated ?? 0} items activated`);
                  fetchItems();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
              }}
              data-testid="bulk-enable-all-btn"
              title="Make every item available for sale"
            >
              Enable All
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-950/20 dark:border-rose-800 dark:text-rose-300"
              onClick={async () => {
                if (!window.confirm('Deactivate ALL menu items? Nothing will be orderable until reactivated.')) return;
                try {
                  const { data } = await api.put('/cashier/menu/bulk-availability', { all: true, is_available: false });
                  toast.success(`${data?.updated ?? 0} items deactivated`);
                  fetchItems();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
              }}
              data-testid="bulk-disable-all-btn"
              title="Hide every item from the POS"
            >
              Disable All
            </Button>
            {selectedItems.length > 0 && (
              <Button variant="outline" className="rounded-xl border-border" onClick={() => setShowBulkAssign(true)} data-testid="bulk-assign-btn"><Building2 size={15} className="mr-1.5" />Assign ({selectedItems.length})</Button>
            )}
            {/* iter598 — Bulk VAT-inclusivity toggle. Lets an admin flip
                "Price includes VAT" on dozens of items at once instead of
                editing each one individually. */}
            {selectedItems.length > 0 && (
              <Button
                variant="outline"
                className="rounded-xl border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/20 dark:border-blue-800 dark:text-blue-300"
                onClick={async () => {
                  const choice = window.confirm(
                    `Mark the ${selectedItems.length} selected item${selectedItems.length === 1 ? '' : 's'} as VAT-inclusive?\n\n` +
                    `OK = Price INCLUDES VAT (back-derives base at POS)\n` +
                    `Cancel = Price EXCLUDES VAT (legacy behaviour)`
                  );
                  // Need a 3-state choice (Cancel vs Close). Re-prompt for "exclude" mode.
                  if (!choice) {
                    const goExclude = window.confirm(
                      'Instead, mark them as VAT-EXCLUSIVE (price does NOT include VAT)?'
                    );
                    if (!goExclude) return;
                    try {
                      const { data } = await api.put('/cashier/menu/bulk-set-vat-inclusive', { item_ids: selectedItems, value: false });
                      toast.success(`${data?.updated ?? 0} items now exclude VAT`);
                      setSelectedItems([]); fetchItems();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
                    return;
                  }
                  try {
                    const { data } = await api.put('/cashier/menu/bulk-set-vat-inclusive', { item_ids: selectedItems, value: true });
                    toast.success(`${data?.updated ?? 0} items now include VAT`);
                    setSelectedItems([]); fetchItems();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
                }}
                data-testid="bulk-vat-inclusive-btn"
                title="Toggle 'Price includes VAT' on the selected items"
              >
                <DollarSign size={15} className="mr-1.5" />VAT ({selectedItems.length})
              </Button>
            )}
            {selectedItems.length > 0 && (
              <Button
                variant="outline"
                className="rounded-xl border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-950/20 dark:border-rose-800 dark:text-rose-300"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-btn"
                title="Permanently delete the selected items"
              >
                <Trash2 size={15} className="mr-1.5" />Delete ({selectedItems.length})
              </Button>
            )}
            <Button variant="outline" className="rounded-xl border-border text-muted-foreground" onClick={() => setShowExport(true)} data-testid="export-menu-btn"><Download size={15} className="mr-1.5" />Export</Button>
            <Button variant="outline" className="rounded-xl border-border text-muted-foreground" onClick={openBranchCost} data-testid="branch-cost-view-btn"><Building2 size={15} className="mr-1.5" />Branch Cost</Button>
            <Button
              variant="outline"
              className="rounded-xl border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-300"
              onClick={() => navigate('/data-import?module=menu_items')}
              data-testid="import-menu-btn"
              title="Bulk import menu items from Excel/CSV (with sizes, add-ons, branch & VAT)"
            >
              <Upload size={15} className="mr-1.5" />Import
            </Button>
            <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90 rounded-xl" data-testid="add-menu-item-btn"><Plus size={15} className="mr-1.5" />Add Item</Button>
          </div>
        </div>

        {/* Menu Phase 2 — Cost Change Alert banner */}
        {costAlerts.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-2xl p-4" data-testid="cost-alert-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <TrendingDown size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">{costAlerts.length} item{costAlerts.length > 1 ? 's' : ''} dropped below 30% margin after a recent purchase</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300/80">Recipe costs rose, so these menu items now make thin (or negative) margins. Review their prices.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {costAlerts.length > 1 && <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setFixAllOpen(true)} data-testid="cost-alert-fix-all">Fix all margins</Button>}
                <Button size="sm" variant="ghost" className="h-8 text-xs text-amber-800" onClick={() => setAlertsOpen(o => !o)} data-testid="cost-alert-toggle">{alertsOpen ? 'Hide' : 'View'}</Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs text-amber-800" onClick={openHistory} data-testid="cost-alert-history-btn">History</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={dismissAlerts} data-testid="cost-alert-dismiss">Dismiss all</Button>
              </div>
            </div>
            {alertsOpen && (
              <div className="mt-3 space-y-1.5 border-t border-amber-200 dark:border-amber-900 pt-3" data-testid="cost-alert-list">
                {costAlerts.map(a => (
                  <div key={a.item_id} className="flex items-center justify-between gap-2 text-xs bg-white/60 dark:bg-black/20 rounded-lg px-2.5 py-1.5">
                    <span className="font-medium flex-1 min-w-0 truncate">{a.name}</span>
                    <span className="text-muted-foreground whitespace-nowrap">cost SR {a.old_cost} → <span className="text-rose-600 font-semibold">SR {a.new_cost}</span> · margin <span className={a.margin < 0 ? 'text-rose-600 font-semibold' : 'text-amber-700 font-semibold'}>{(a.margin * 100).toFixed(0)}%</span> @ SR {a.price}</span>
                    <Button size="sm" className="h-6 text-[11px] bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => openFixPreview(a)} data-testid={`fix-margin-${a.item_id}`}>Fix Margin</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Foodics-style filter bar */}
        <div className="bg-white dark:bg-muted rounded-2xl shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-3 text-muted-foreground" />
              <Input placeholder="Search items..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 rounded-xl border-border" />
            </div>
            <div className="flex gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-36 rounded-xl border-border" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="icon" variant="outline" className="h-10 w-10 shrink-0 rounded-xl border-border" onClick={() => setShowCategoryDialog(true)} data-testid="manage-categories-btn"><Tag size={15} /></Button>
              <Button size="icon" variant="outline" className="h-10 w-10 shrink-0 rounded-xl border-border" onClick={() => setShowDuplicateDialog(true)} title="Duplicate menu to another branch" data-testid="duplicate-menu-btn"><Copy size={15} /></Button>
            </div>
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-full sm:w-36 rounded-xl border-border"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-full sm:w-36 rounded-xl border-border"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.filter(p => p.name !== 'Other').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border dark:border-stone-700">
            <Checkbox checked={selectedItems.length > 0 && selectedItems.length === filteredItems.length} onCheckedChange={selectAll} />
            <span className="text-xs text-muted-foreground">{selectedItems.length > 0 ? `${selectedItems.length} selected` : `${filteredItems.length} items`}</span>
          </div>
          {/* iter635 — Foodics-style status tabs (All / Active / Inactive). */}
          <div className="flex items-center justify-between gap-2 mt-3 border-t border-border dark:border-stone-700 pt-2" data-testid="menu-status-tabs">
            <div className="flex items-center gap-1">
              {[
                { id: 'all', label: 'All' },
                { id: 'active', label: 'Active' },
                { id: 'inactive', label: 'Inactive' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setStatusTab(t.id)}
                  className={`px-3 py-1 text-xs rounded-md transition ${statusTab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                  data-testid={`menu-status-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* iter638 — view-mode toggle: Foodics-style list (default) vs card grid. */}
            <div className="flex items-center gap-0.5 border border-border dark:border-stone-700 rounded-md p-0.5" data-testid="menu-view-toggle">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                data-testid="menu-view-list"
                title="Foodics-style list view"
              >
                <ListIcon size={14} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded transition ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                data-testid="menu-view-grid"
                title="Card grid view"
              >
                <LayoutGrid size={14} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={28} className="animate-spin text-primary" /></div>
        ) : viewMode === 'list' ? (
          /* iter638 — Foodics-style list view. Clean white surface, thin
             dividers, generous spacing, single primary action per row. */
          <div className="bg-white dark:bg-muted rounded-2xl shadow-sm overflow-hidden" data-testid="menu-list-view">
            <div className="hidden md:grid grid-cols-[36px_56px_minmax(0,2.5fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)_120px_92px] gap-3 px-4 py-3 border-b border-border dark:border-stone-700 bg-stone-50/60 dark:bg-stone-900/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span></span>
              <span></span>
              <span>Item</span>
              <span>Category</span>
              <span className="text-right">Price</span>
              <span>Availability</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
            {pagedItems.map((item, idx) => {
              const isSel = selectedItems.includes(item.id);
              const brCount = item.branch_ids?.length || 0;
              const branchSummary = brCount === 0 ? 'All branches' : brCount === 1 ? getBranchName(item.branch_ids[0]) : `${brCount} branches`;
              return (
                <MenuListRow
                  key={item.id}
                  item={item}
                  idx={idx}
                  totalRows={pagedItems.length}
                  isSel={isSel}
                  branchSummary={branchSummary}
                  priceEdit={priceEdit}
                  setPriceEdit={setPriceEdit}
                  savePriceEdit={savePriceEdit}
                  changeCategory={changeCategory}
                  toggleAvailability={toggleAvailability}
                  toggleSelect={toggleSelect}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  getImageUrl={getImageUrl}
                  cur={cur}
                  categories={CATEGORIES}
                  recipeCost={getRecipeCost(item)}
                />
              );
            })}
            {filteredItems.length === 0 && <div className="text-center py-16 text-muted-foreground text-sm">No items found</div>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {pagedItems.map(item => (
              <MenuGridCard
                key={item.id}
                item={item}
                isSel={selectedItems.includes(item.id)}
                toggleSelect={toggleSelect}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleImageUpload={handleImageUpload}
                getImageUrl={getImageUrl}
                getBranchName={getBranchName}
                getPlatformName={getPlatformName}
                getModifierCount={getModifierCount}
                getModifierLabels={getModifierLabels}
                cur={cur}
                fileInputRef={fileInputRef}
                uploadItemRef={uploadItemRef}
                recipeCost={getRecipeCost(item)}
              />
            ))}
            {filteredItems.length === 0 && <div className="col-span-full text-center py-16 text-muted-foreground">No items found</div>}
          </div>
        )}

        {/* Menu Phase 1 — pagination controls. Search/filters run across the
            full item set (loaded via /cashier/menu-all), then we page the
            already-filtered result so large menus stay snappy. */}
        {filteredItems.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4" data-testid="menu-pagination">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Rows per page</span>
              <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                <SelectTrigger className="h-8 w-[72px]" data-testid="menu-page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="ml-1" data-testid="menu-page-range">
                {filteredItems.length === 0 ? 0 : safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filteredItems.length)} of {filteredItems.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="menu-page-prev">Prev</Button>
              <span className="text-xs text-muted-foreground">Page {safePage + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={safePage + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="menu-page-next">Next</Button>
            </div>
          </div>
        )}

        {/* iter645 — Bulk-edit toolbar (Foodics-style). Renders inside the
            page content area so it stays anchored to the menu list, not
            the entire viewport. Auto-hides when no row is selected. */}
        <BulkEditToolbar
          selectedCount={selectedItems.length}
          onClear={() => setSelectedItems([])}
          categories={CATEGORIES}
          onApplyDiscount={bulkApplyDiscount}
          onMoveCategory={bulkMoveCategory}
          onSetAvailability={bulkSetAvailability}
          onDelete={bulkDelete}
          onAiFill={bulkAiFill}
          aiFilling={aiFilling}
          onLinkRecipe={() => setLinkRecipeOpen(true)}
          onSetAutoCost={bulkSetAutoCost}
          saving={bulkSaving}
        />
      </div>

      {/* iter645 — Single hidden file input shared across all grid cards
          (image upload). The card pushes its item into uploadItemRef
          then triggers fileInputRef.current?.click(). */}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={fileInputRef}
        onChange={e => { if (e.target.files[0] && uploadItemRef.current) handleImageUpload(uploadItemRef.current, e.target.files[0]); }}
      />

      {/* iter643/iter653 — Foodics-style right-side edit drawer (extracted to its own component). */}
      <MenuItemEditSheet
        open={showDialog}
        onOpenChange={setShowDialog}
        editingItem={editingItem}
        formData={formData}
        setFormData={setFormData}
        saving={saving}
        onSave={handleSave}
        branches={branches}
        platforms={platforms}
        allergenChoices={allergenChoices}
        centralAddons={centralAddons}
        categories={CATEGORIES}
        cur={cur}
        suggestedPriceBanner={suggestedPriceBanner}
        setSuggestedPriceBanner={setSuggestedPriceBanner}
        toggleBranch={toggleBranch}
        togglePlatform={togglePlatform}
        SizesEditor={SizesEditor}
        AddonsLinker={AddonsLinker}
        OptionGroupsEditor={OptionGroupsEditor}
        recipes={recipes}
        onAddCategory={createCategoryInline}
      />

      {/* Bulk Assign Dialog */}

      {/* iter879 — Duplicate Menu to Another Branch */}
      <DuplicateMenuDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        branches={branches}
        onDone={() => fetchItems()}
      />

      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-outfit">Bulk Assign ({selectedItems.length} items)</DialogTitle></DialogHeader>
          <Tabs defaultValue="branches">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="branches" className="flex-1">Branches</TabsTrigger>
              <TabsTrigger value="platforms" className="flex-1">Platforms</TabsTrigger>
            </TabsList>
            <TabsContent value="branches" className="space-y-2">
              {branches.map(b => (
                <div key={b.id} className="flex items-center gap-3 p-2 border rounded-lg dark:border-stone-700 cursor-pointer hover:bg-muted/50 dark:hover:bg-muted"
                  onClick={() => setBulkBranches(prev => prev.includes(b.id) ? prev.filter(i => i !== b.id) : [...prev, b.id])}>
                  <Checkbox checked={bulkBranches.includes(b.id)} onCheckedChange={() => setBulkBranches(prev => prev.includes(b.id) ? prev.filter(i => i !== b.id) : [...prev, b.id])} />
                  <Building2 size={14} /><span className="text-sm">{b.name}</span>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="platforms" className="space-y-2">
              {platforms.filter(p => p.name !== 'Other').map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2 border rounded-lg dark:border-stone-700 cursor-pointer hover:bg-muted/50 dark:hover:bg-muted"
                  onClick={() => setBulkPlatforms(prev => prev.includes(p.id) ? prev.filter(i => i !== p.id) : [...prev, p.id])}>
                  <Checkbox checked={bulkPlatforms.includes(p.id)} onCheckedChange={() => setBulkPlatforms(prev => prev.includes(p.id) ? prev.filter(i => i !== p.id) : [...prev, p.id])} />
                  <Globe size={14} /><span className="text-sm">{p.name}</span>
                </div>
              ))}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssign(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} className="bg-orange-500 hover:bg-orange-600"><Check size={16} className="mr-1" />Apply to {selectedItems.length} items</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Menu Dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><Download size={18} className="text-orange-500" />Export Menu for Platform</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Platform</Label>
              <Select value={exportPlatformId} onValueChange={setExportPlatformId}>
                <SelectTrigger><SelectValue placeholder="Choose platform..." /></SelectTrigger>
                <SelectContent>
                  {platforms.filter(p => p.name !== 'Other').map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.name_ar})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExport(false)}>Cancel</Button>
            <Button onClick={handleExport} disabled={!exportPlatformId} className="bg-orange-500 hover:bg-orange-600"><Download size={16} className="mr-1" />Export Menu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-lg max-h-[85dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-outfit">Manage Menu Categories</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Upload a custom <b>cover image</b> per category — it replaces the auto-generated tile in the Cashier POS. Covers are auto-derived from the top item&apos;s photo if you leave them blank.
            </p>
          </DialogHeader>
          <div className="flex gap-2 shrink-0">
            <Input placeholder="New category name..." value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCategory()} className="flex-1" data-testid="new-category-input" />
            <Button onClick={addCategory} disabled={savingCategory || !newCategoryName.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="add-category-btn">
              {savingCategory ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            </Button>
          </div>
          {/* iter877 — Branch scope chips. Empty selection = all branches. */}
          {branches?.length > 0 && (
            <div className="shrink-0 space-y-1.5" data-testid="new-category-branch-chips">
              <p className="text-[11px] text-muted-foreground">Visible in branches:{' '}
                {newCategoryBranchIds.length === 0
                  ? <span className="text-emerald-600 font-medium">all branches</span>
                  : <span className="font-medium">{newCategoryBranchIds.length} selected</span>}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {branches.map(b => {
                  const on = newCategoryBranchIds.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setNewCategoryBranchIds(prev => on ? prev.filter(x => x !== b.id) : [...prev, b.id])}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${on ? 'bg-orange-500 text-white border-orange-500' : 'bg-white hover:bg-orange-50 border-border text-foreground'}`}
                      data-testid={`new-category-branch-chip-${b.id}`}
                    >
                      {b.name}
                    </button>
                  );
                })}
                {newCategoryBranchIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setNewCategoryBranchIds([])}
                    className="text-[11px] text-muted-foreground hover:text-foreground underline"
                    data-testid="new-category-branch-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="space-y-4 overflow-y-auto pr-1 flex-1 min-h-0" data-testid="categories-scroll">
            {/* Defaults row (iter379 — each has its own cover slot too) */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Default Categories</p>
              <div className="space-y-1.5">
                {DEFAULT_CATEGORIES.map(c => {
                  const pos = (posCategoryList || []).find(p => p.id === c.id) || {};
                  return (
                    <CategoryCoverRow
                      key={c.id}
                      catId={c.id}
                      name={c.name}
                      pos={pos}
                      onUpload={uploadCategoryCover}
                      onRemove={removeCategoryCover}
                      toUrl={toBackendImageUrl}
                      onAiCover={generateCategoryCover}
                      coverBusy={coverBusy}
                      rightSlot={<Badge variant="outline" className="text-[10px]">Built-in</Badge>}
                    />
                  );
                })}
              </div>
            </div>
            {customCategories.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Custom Categories ({customCategories.length})</p>
                <div className="space-y-1.5">
                  {customCategories.map(c => {
                    const cid = c.name.toLowerCase().replace(/\s+/g, '_');
                    const pos = (posCategoryList || []).find(p => p.id === cid) || {};
                    return (
                      <CategoryCoverRow
                        key={c.id}
                        catId={cid}
                        name={c.name}
                        pos={pos}
                        onUpload={uploadCategoryCover}
                        onRemove={removeCategoryCover}
                        toUrl={toBackendImageUrl}
                        onAiCover={generateCategoryCover}
                        coverBusy={coverBusy}
                        rightSlot={
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error hover:text-error hover:bg-error/10"
                            onClick={() => deleteCategory({ dbId: c.id, name: c.name })} data-testid={`delete-cat-${c.id}`}><Trash2 size={14} /></Button>
                        }
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Phase 2 — Branch Cost View */}
      <Dialog open={showBranchCost} onOpenChange={setShowBranchCost}>
        <DialogContent className="max-w-3xl" data-testid="branch-cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2"><Building2 size={18} />Branch Cost vs. Price</DialogTitle>
          </DialogHeader>
          {!branchCostData ? (
            <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{branchCostData.total} priced rows · <span className={branchCostData.below_cost_count ? 'text-rose-600 font-semibold' : ''}>{branchCostData.below_cost_count} below cost</span></span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs" data-testid="margin-target-wrapper">
                    <span className="text-muted-foreground">Target margin</span>
                    <Select value={String(marginTarget)} onValueChange={updateMarginTarget}>
                      <SelectTrigger className="h-7 w-[76px]" data-testid="margin-target-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0.20, 0.25, 0.30, 0.35, 0.40].map(t => <SelectItem key={t} value={String(t)}>{Math.round(t * 100)}%</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="branch-cost-below-only">
                    <input type="checkbox" checked={belowOnly} onChange={e => setBelowOnly(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                    Below-cost only
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs bg-muted/40 rounded-lg p-2" data-testid="per-category-target">
                <span className="text-muted-foreground">Per-category target:</span>
                <Select value={catTargetCat} onValueChange={setCatTargetCat}>
                  <SelectTrigger className="h-7 w-[150px]" data-testid="cat-target-category"><SelectValue placeholder="Pick category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{byCategory[c.id] != null ? ` · ${Math.round(byCategory[c.id] * 100)}%` : ''}</SelectItem>)}</SelectContent>
                </Select>
                {catTargetCat && (
                  <Select value={String(byCategory[catTargetCat] ?? '')} onValueChange={(v) => setCategoryTarget(catTargetCat, v)}>
                    <SelectTrigger className="h-7 w-[90px]" data-testid="cat-target-value"><SelectValue placeholder="Set %" /></SelectTrigger>
                    <SelectContent>{[0.20, 0.25, 0.30, 0.35, 0.40, 0.45, 0.50].map(t => <SelectItem key={t} value={String(t)}>{Math.round(t * 100)}%</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {catTargetCat && byCategory[catTargetCat] != null && (
                  <button className="text-rose-500 hover:underline" onClick={async () => { await api.delete(`/menu-settings/margin-target/${catTargetCat}`); setByCategory(p => { const n = { ...p }; delete n[catTargetCat]; return n; }); toast.success('Reverted to global target'); openBranchCost(); }} data-testid="cat-target-clear">clear</button>
                )}
              </div>
              <div className="max-h-[55vh] overflow-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 sticky top-0">
                    <tr className="text-left"><th className="px-3 py-2">Item</th><th className="px-3 py-2">Branch</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Margin</th></tr>
                  </thead>
                  <tbody>
                    {(branchCostData.rows || []).filter(r => !belowOnly || r.below_cost).slice(0, 300).map((r, i) => (
                      <tr key={i} className={`border-t ${r.below_cost ? 'bg-rose-50 dark:bg-rose-950/20' : ''}`} data-testid={`branch-cost-row-${i}`}>
                        <td className="px-3 py-1.5 font-medium">{r.item_name}</td>
                        <td className="px-3 py-1.5">{r.branch_name}</td>
                        <td className="px-3 py-1.5 text-right">{r.price.toFixed(2)}{r.price_source !== 'global' && <span className="text-[9px] text-blue-500 ml-1">*</span>}</td>
                        <td className="px-3 py-1.5 text-right">{r.cost.toFixed(2)}</td>
                        <td className={`px-3 py-1.5 text-right font-semibold ${r.below_cost ? 'text-rose-600' : r.thin ? 'text-amber-600' : 'text-emerald-600'}`}>{(r.margin * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                    {(branchCostData.rows || []).filter(r => !belowOnly || r.below_cost).length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">{belowOnly ? 'No items selling below cost 🎉' : 'No priced items with a cost yet.'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground">* branch-specific price (override or per-branch price). Rows highlighted red sell below their recipe/manual cost.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Menu Phase 2 — Fix All margins preview */}
      <Dialog open={fixAllOpen} onOpenChange={setFixAllOpen}>
        <DialogContent className="max-w-lg" data-testid="fix-all-dialog">
          <DialogHeader><DialogTitle className="font-outfit">Fix all margins ({costAlerts.length} items)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Each item will be repriced to its target margin (per-category where set) across ALL branches.</p>
            <div className="max-h-[50vh] overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0"><tr className="text-left"><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Now</th><th className="px-3 py-2 text-right">New</th><th className="px-3 py-2 text-right">Target</th></tr></thead>
                <tbody>
                  {fixAllRows().map((r, i) => (
                    <tr key={r.item_id} className="border-t" data-testid={`fix-all-row-${i}`}>
                      <td className="px-3 py-1.5 font-medium">{r.name}</td>
                      <td className="px-3 py-1.5 text-right text-muted-foreground">SR {r.price}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-emerald-600">SR {r.newPrice}</td>
                      <td className="px-3 py-1.5 text-right">{Math.round(r.t * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setFixAllOpen(false)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmFixAll} data-testid="fix-all-confirm">Apply all</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Phase 2 — Fix-Margin preview */}
      <Dialog open={!!fixPreview} onOpenChange={(o) => { if (!o) setFixPreview(null); }}>
        <DialogContent className="max-w-md" data-testid="fix-margin-dialog">
          <DialogHeader><DialogTitle className="font-outfit">Reprice to {Math.round(marginTarget * 100)}% margin</DialogTitle></DialogHeader>
          {fixPreview && (
            <div className="space-y-3 text-sm">
              <p className="font-semibold">{fixPreview.alert.name}</p>
              <div className="grid grid-cols-2 gap-2 text-xs bg-muted/50 rounded-lg p-3">
                <div><span className="text-muted-foreground">Recipe cost</span><div className="font-semibold">SR {fixPreview.alert.new_cost}</div></div>
                <div><span className="text-muted-foreground">Current price</span><div className="font-semibold">SR {fixPreview.alert.price}</div></div>
                <div><span className="text-muted-foreground">Current margin</span><div className="font-semibold text-rose-600">{(fixPreview.alert.margin * 100).toFixed(0)}%</div></div>
                <div><span className="text-muted-foreground">Proposed price</span><div className="font-semibold text-emerald-600" data-testid="fix-margin-proposed">SR {fixPreview.newPrice}</div></div>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 rounded-md p-2">Applies across ALL branches — any branch price or override below SR {fixPreview.newPrice} will be lifted to this price.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setFixPreview(null)}>Cancel</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmFixMargin} data-testid="fix-margin-confirm">Apply SR {fixPreview.newPrice}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Menu Phase 2 — Cost Alert History */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl" data-testid="cost-alert-history-dialog">
          <DialogHeader><DialogTitle className="font-outfit">Cost Alert History</DialogTitle></DialogHeader>
          {historyData && (historyData.history || []).length > 0 && (
            <div className="flex justify-end -mt-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportHistory} data-testid="history-export-csv"><Download size={12} className="mr-1" />Export CSV</Button>
            </div>
          )}
          {!historyData ? (
            <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
          ) : (historyData.history || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No resolved alerts yet.</div>
          ) : (
            <div className="max-h-[55vh] overflow-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0"><tr className="text-left"><th className="px-3 py-2">When</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Action</th><th className="px-3 py-2 text-right">Price</th></tr></thead>
                <tbody>
                  {historyData.history.map((h, i) => (
                    <tr key={i} className="border-t" data-testid={`history-row-${i}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{h.resolved_at ? new Date(h.resolved_at).toLocaleString() : '-'}</td>
                      <td className="px-3 py-1.5 font-medium">{h.name}</td>
                      <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${h.action === 'repriced' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>{h.action}</span></td>
                      <td className="px-3 py-1.5 text-right">{h.action === 'repriced' ? <span>SR {h.old_price} → <span className="font-semibold text-emerald-600">SR {h.new_price}</span></span> : <span className="text-muted-foreground">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Menu Phase 2 — Bulk Recipe Link */}
      <Dialog open={linkRecipeOpen} onOpenChange={setLinkRecipeOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-link-recipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Link recipe to {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipe</Label>
              <Select value={linkRecipeId} onValueChange={setLinkRecipeId}>
                <SelectTrigger data-testid="bulk-link-recipe-select"><SelectValue placeholder="Choose a recipe…" /></SelectTrigger>
                <SelectContent>
                  {recipes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.cost_per_unit != null ? ` · SR ${Number(r.cost_per_unit).toFixed(2)}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Scale (portions per item)</Label>
              <Input type="number" step="0.1" value={linkScale} onChange={e => setLinkScale(e.target.value)} data-testid="bulk-link-recipe-scale" />
            </div>
            <p className="text-[11px] text-muted-foreground">Sets this as the default recipe link and switches all selected items to auto-cost from the recipe.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setLinkRecipeOpen(false)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={linking || !linkRecipeId} onClick={doBulkRecipeLink} data-testid="bulk-link-recipe-confirm">
                {linking ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FolderInput size={14} className="mr-1.5" />}Link & auto-cost
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

/**
 * CategoryCoverRow — single row inside Manage Categories dialog (iter379).
 * Shows a 40x40 cover thumbnail (manual override OR auto-derived OR a
 * fallback icon), plus Upload and Remove buttons.
 */
function CategoryCoverRow({ catId, name, pos, onUpload, onRemove, toUrl, rightSlot, onAiCover, coverBusy }) {
  const fileRef = useRef(null);
  const url = toUrl(pos.image_url);
  const source = pos.image_source; // 'manual' | 'auto' | 'ai' | null
  const busy = coverBusy === catId;
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-muted/40 rounded-lg" data-testid={`cat-row-${catId}`}>
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-700 dark:to-stone-600 flex items-center justify-center shrink-0">
        {url ? (
          <img src={url} alt={name} className="w-full h-full object-cover" />
        ) : (
          <UtensilsCrossed size={16} className="text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        <div className="text-[10px] text-muted-foreground">
          {source === 'manual' ? 'Manual cover' : source === 'ai' ? 'AI cover' : source === 'auto' ? 'Auto cover (top item)' : 'No cover yet'}
        </div>
      </div>
      {/* iter605 — Per-category routing target: which prep printer this
          category prints to (Kitchen, Bar, or None). Stored on the
          category doc and read by the POS when fanning out tickets. */}
      <PrintToSelect catId={catId} value={pos?.print_to || 'kitchen'} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) { onUpload(catId, e.target.files[0]); e.target.value = ''; } }} data-testid={`cat-cover-input-${catId}`} />
      {onAiCover && (
        <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs text-indigo-600 border-indigo-200" disabled={busy} onClick={() => onAiCover(catId, name)} data-testid={`cat-cover-ai-${catId}`}>
          {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />}AI
        </Button>
      )}
      <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => fileRef.current?.click()} data-testid={`cat-cover-upload-${catId}`}>
        <Upload size={12} className="mr-1" />{source === 'manual' ? 'Change' : 'Upload'}
      </Button>
      {source === 'manual' && (
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-500" onClick={() => onRemove(catId)} data-testid={`cat-cover-remove-${catId}`} title="Revert to auto cover">
          <X size={14} />
        </Button>
      )}
      {rightSlot}
    </div>
  );
}

// iter605/606 — Compact pill that lets the manager pick where this menu
// category prints prep tickets. Dynamically lists every configured
// kitchen + bar station (e.g., "Kitchen — Cold Prep", "Kitchen — Hot
// Grill", "Bar — Cocktails") so multi-kitchen branches can route
// salads to one printer and pizzas to another. PUTs to
// /cashier/categories/{id}.
function PrintToSelect({ catId, value }) {
  const [val, setVal] = useState(value || 'kitchen');
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    api.get('/cashier/printers/routing-options')
      .then((res) => {
        if (cancelled) return;
        setOptions(res?.data?.options || []);
      })
      .catch(() => { if (!cancelled) setOptions([]); });
    return () => { cancelled = true; };
  }, []);

  const onChange = async (next) => {
    setVal(next);
    setSaving(true);
    try {
      await api.put(`/cashier/categories/${catId}`, { print_to: next });
      const label = (options || []).find(o => o.value === next)?.label || next;
      toast.success(`Routing saved: ${label}`);
    } catch {
      toast.error('Could not save routing');
      setVal(value || 'kitchen');
    } finally {
      setSaving(false);
    }
  };

  // Colour tone reflects the chosen role.
  const role = val.startsWith('bar') ? 'bar' : val === 'none' ? 'none' : 'kitchen';
  const tone = role === 'bar'
    ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
    : role === 'none'
      ? 'bg-slate-50 border-slate-200 text-slate-600'
      : 'bg-orange-50 border-orange-200 text-orange-700';

  // Fallback options while the API call is in flight (keeps the UI
  // responsive on slow connections).
  const opts = options ?? [
    { value: 'none', label: 'None' },
    { value: 'kitchen', label: 'Kitchen (any station)' },
    { value: 'bar', label: 'Bar (any station)' },
  ];
  // Make sure the current value appears even if the API didn't list it
  // (e.g., the station printer was just deleted).
  const hasCurrent = opts.some(o => o.value === val);
  const finalOpts = hasCurrent ? opts : [...opts, { value: val, label: val }];

  return (
    <label
      className={`text-[10px] font-semibold rounded-lg px-2 py-1 border cursor-pointer flex items-center gap-1 ${tone} ${saving ? 'opacity-60' : ''}`}
      data-testid={`cat-print-to-${catId}`}
      title="Where this category prints its prep ticket"
    >
      <span>Print:</span>
      <select
        value={val}
        onChange={(e) => onChange(e.target.value)}
        disabled={saving}
        className="bg-transparent font-semibold outline-none cursor-pointer max-w-[140px] truncate"
        data-testid={`cat-print-to-select-${catId}`}
      >
        {finalOpts.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
